import { Avatar, AvatarFallback, AvatarImage } from '@workspace/ui/components/avatar'
import { EmptyState } from '@workspace/ui/components/empty-state'
import { getInitials } from '@workspace/ui/lib/utils'
import { Users } from 'lucide-react'

type Member = {
  id: string
  name: string | null
  position?: string | null
  role?: string | null
  imageUrl: string | null
  linkedinUrl: string | null
}

export type TeamListProps = {
  members: Member[]
}

export function TeamList({ members }: TeamListProps) {
  if (members.length === 0) {
    return <EmptyState icon={<Users className="size-8" />} title="No team members added" />
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {members.map((member) => {
        const hasLinkedIn = member.linkedinUrl && member.linkedinUrl.trim() !== ''
        const content = (
          <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-input/30">
            <Avatar className="size-10 ring-2 ring-border shrink-0">
              {member.imageUrl && <AvatarImage src={member.imageUrl} alt={member.name ?? ''} />}
              <AvatarFallback className="bg-input text-sm font-medium text-white">
                {getInitials(member.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-white text-sm truncate">{member.name}</p>
              <p className="text-xs text-muted-foreground">{member.position ?? member.role}</p>
            </div>
          </div>
        )

        if (hasLinkedIn && member.linkedinUrl) {
          return (
            <a
              key={member.id}
              href={member.linkedinUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:ring-2 hover:ring-primary/50 rounded-lg transition-all"
            >
              {content}
            </a>
          )
        }

        return <div key={member.id}>{content}</div>
      })}
    </div>
  )
}
